import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import YearTransitionBanner from './components/YearTransitionBanner';
import type { GameVersion } from './types';

const BOOTSTRAP_FADE_DURATION_MS = 600;
const BOOTSTRAP_REVEAL_DELAY_MS = 120;

const GameBootstrap: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { version } = useParams<{ version: string }>();
  const gameVersion: GameVersion = version === 'long' ? 'long' : 'short';
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [isVisible, setIsVisible] = useState(false);
  const [isBlackOverlayVisible, setIsBlackOverlayVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const revealTimer = window.setTimeout(() => {
      setIsVisible(true);
      setIsBlackOverlayVisible(false);
    }, BOOTSTRAP_REVEAL_DELAY_MS);

    return () => clearTimeout(revealTimer);
  }, []);

  const handleBegin = useCallback(() => {
    if (isExiting) {
      return;
    }

    const existingSessionId = searchParams.get('session_id');
    const sessionId = existingSessionId || crypto.randomUUID();
    setIsVisible(false);
    setIsExiting(true);
    setIsBlackOverlayVisible(true);

    window.setTimeout(() => {
      const query = searchParams.toString();
      navigate(query ? `/game/play/${gameVersion}/${sessionId}?${query}` : `/game/play/${gameVersion}/${sessionId}`);
    }, BOOTSTRAP_FADE_DURATION_MS);
  }, [gameVersion, isExiting, navigate, searchParams]);

  return (
    <div
      className="relative flex h-screen max-h-screen min-h-0 w-full items-center justify-center overflow-hidden bg-black bg-cover bg-center"
      style={{
        backgroundImage: "url('/backgrounds/game-transition-bg.svg')",
      }}
    >
      <div
        className={`relative flex flex-col items-center gap-6 transition-opacity duration-500 ease-in-out ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <YearTransitionBanner nextYear={1} />

        <button
          onClick={handleBegin}
          disabled={isExiting}
          className="px-8 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {gameVersion === 'long' ? 'Begin Extended Trial' : 'Begin Trial'}
        </button>
      </div>

      <div
        className={`pointer-events-none absolute inset-0 bg-black transition-opacity duration-600 ease-in-out ${
          isBlackOverlayVisible ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
};

export default GameBootstrap;